import {
	Avatar,
	Box,
	ButtonBase,
	Paper,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import React from 'react';
import stylesLogo from './Account.styles';
import AddNewButton from '../Buttons/AddNewButton';
import { useState, useContext, useMemo } from 'react';
import AccountCheckboxContainer from './AccountCheckboxContainer';
import linkedAccountsFIStyles from './LinkedAccountsFI.styles';
import sizeFontCustom from '../Discovery/fontSize.styles';
import StepperContext from '../Context/StepperContext';
import { useTranslation } from '../../hooks/useTranslation';
import AuthApis from '../../hepler/authApis.service';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';

function LinkedAccountsFI(props) {
	const { t } = useTranslation();
	const { prevStep } = useContext(StepperContext);
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const redirect = () => {
		global.fi = global.FIGroups?.[name];
		delete global.isTargetedDiscoveryMode;
		global.userClickedAddMore = true;
		global.hasReachedConsent = true;
		global.isAddAccount = true;
		props.history.push(
			`/home/?number=${global.mobileNumber}&isAddAccount=${true}`,
			{ fiTypes: global.FIGroups[name] }
		);
	};
	const {
		linkedAccounts,
		name,
		fIGroupsWithZeroAccts,
		consentHandle,
		superState,
	} = props;
	const handleAddAccount = () => {
		prevStep();
		redirect();
	};
	const [isExpanded, setIsExpanded] = useState(false);
	const authApis = useMemo(() => new AuthApis(), []);
	const isMandatoryConsent =
		props.consent && !authApis.isCheckboxRequired(props.consent);
	const isActiveFlag = isMandatoryConsent
		? true
		: Boolean(
				props?.selectedConsentsState?.includes(props?.consentHandle),
			);
	const isTargetedAccountDiscovery =
		global.redirectAccountRestrictions && !global.userClickedAddMore;

	const selectedAccounts = superState?.consentSelectedAccounts?.[
		consentHandle
	]?.filter((account) => global.FIGroups?.[name]?.includes(account?.FIType));

	const requiredAccounts = linkedAccounts?.filter((account) =>
		global.FIGroups?.[name]?.includes(account?.FIType),
	);
	const accountsLinkedFlag = requiredAccounts?.length > 0;
	const showFiSelectAll = accountsLinkedFlag && !isTargetedAccountDiscovery;
	const isBankAccountsSection = name === 'Bank Accounts';
	const accordionAccountsDetails = !isBankAccountsSection
		? mobileScreen
			? 1
			: 3
		: requiredAccounts?.length
		? requiredAccounts?.length
		: fIGroupsWithZeroAccts?.length;

	const renderExpandableList = (accounts, isZero) => {
		const total = accounts?.length ?? 0;
		const initialVisible = accordionAccountsDetails;
		const hasMore = total > initialVisible;
		const visibleEnd = isExpanded || !hasMore ? total : initialVisible;

		return (
			<Box>
				<AccountCheckboxContainer
					requiredAccounts={accounts}
					isZero={isZero}
					start={0}
					end={visibleEnd}
					accountRestrictions={props.accountRestrictions}
					{...props}
				/>
				{hasMore && (
					<Box textAlign="start" mt="0.5rem">
						<ButtonBase
							disabled={!isActiveFlag}
							sx={{
								...sizeFontCustom.altHeading,
								color:
									props.customDetails?.primaryTextColor ??
									(isDark ? '#fff' : theme.palette.primary.main),
								borderRadius: '4px',
								p: '0.25rem',
								...keyboardFocusVisibleSx,
							}}
							onClick={() => setIsExpanded((prev) => !prev)}
						>
							{isExpanded
								? t('common.buttons.showLess')
								: `+ ${total - initialVisible} ${t('common.buttons.showMore')}`}
						</ButtonBase>
					</Box>
				)}
			</Box>
		);
	};

	return (
		<Paper
			sx={{
				...stylesLogo.paper,
				backgroundColor: isDark ? '#161616' : '#fff',
				color: isDark ? '#fff' : '#000',
			}}
		>
			<Box>
				<Box sx={linkedAccountsFIStyles.headingContainer}>
					<Typography
						noWrap
						component="h2"
						variant="h6"
						sx={{
							...linkedAccountsFIStyles.name,
							color:
								props.customDetails?.primaryTextColor ??
								(isDark ? '#fff' : '#000'),
						}}
					>
						{name}
						{accountsLinkedFlag && (
							<Avatar
								sx={{
									...linkedAccountsFIStyles.count,
									bgcolor: isDark ? '#7A8185' : theme.palette.info.light,
									color: isDark ? '#161616' : '#5C6068',
								}}
							>
								{requiredAccounts.length}
							</Avatar>
						)}
					</Typography>
					{showFiSelectAll && (
						<ButtonBase
							type="button"
							disabled={!isActiveFlag}
							disableRipple
							aria-label={t('common.buttons.selectAll')}
							sx={{
								...sizeFontCustom.altHeading,
								color: !isActiveFlag
									? theme.palette.action.disabled
									: (props.customDetails?.primaryColor ??
										(isDark ? '#fff' : '#000')),
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
								whiteSpace: 'nowrap',
								ml: 'auto',
								flexShrink: 0,
								borderRadius: '4px',
								p: '0.25rem',
								'& .MuiSvgIcon-root': {
									fill: !isActiveFlag
										? theme.palette.action.disabled
										: (props.customDetails?.primaryColor ??
											theme.palette.primary.main),
								},
								'&:hover': {
									backgroundColor: 'transparent !important',
								},
								...keyboardFocusVisibleSx,
							}}
							onClick={() =>
								props.selectUnselectAllFIAccounts(
									requiredAccounts,
									name,
									props.consentHandle,
								)
							}
						>
							{t('common.buttons.selectAll')}
							{selectedAccounts?.length === requiredAccounts.length &&
							requiredAccounts.length > 0 ? (
								<CheckBoxIcon />
							) : selectedAccounts?.length > 0 &&
							  selectedAccounts.length < requiredAccounts.length ? (
								<IndeterminateCheckBoxIcon />
							) : (
								<CheckBoxOutlineBlankIcon />
							)}
						</ButtonBase>
					)}
				</Box>
				<>
					{!isBankAccountsSection && accountsLinkedFlag
						? renderExpandableList(requiredAccounts, false)
						: (
							<AccountCheckboxContainer
								requiredAccounts={requiredAccounts}
								isZero={false}
								start={0}
								end={accordionAccountsDetails}
								accountRestrictions={props.accountRestrictions}
								{...props}
							/>
						)}
					{accountsLinkedFlag &&
						!global.hideAddNew &&
						!(
							global.redirectAccountRestrictions &&
							global.sdkOptions?.disabledAddNew === 'true'
						) && (
							<AddNewButton
								customDetails={props.customDetails}
								isActiveFlag={isActiveFlag}
								handleAddAccount={handleAddAccount}
								buttonStyles={{ marginTop: '1rem' }}
							/>
						)}
					{!accountsLinkedFlag &&
						fIGroupsWithZeroAccts?.length &&
						renderExpandableList(fIGroupsWithZeroAccts, true)}
				</>
			</Box>
		</Paper>
	);
}

export default LinkedAccountsFI;
